import Service, { service } from '@ember/service';
import type { Store } from '@warp-drive/core';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import { executeSearchRequest } from 'tio-data/requests/search';
import { OpenSearchResponseBuilder } from '../utils/open-search-response-builder.ts';

const RECONCILIATION_BUDGET_MS = 10_000;
const MAX_RETRIES = 8;
const MAX_BACKOFF_MS = 1_000;

type ApplicationProjection = {
  tasApplicationIdKeyword?: string;
  tasApplicationLatestStateChangeAtDate?: string;
};

/** Reconciles successful approvals with the queue's asynchronously indexed projection. */
export default class ApprovalQueueRefreshService extends Service {
  @service declare store: Store;

  #approvals = new Map<string, number>();

  /** Records the server transition timestamp from an explicit post-approval application reload. */
  recordApproval = (
    applicationId: string | null | undefined,
    transitionTimestamp: string | null | undefined
  ) => {
    const timestamp = Date.parse(transitionTimestamp ?? '');
    if (applicationId && Number.isFinite(timestamp)) {
      this.#approvals.set(
        applicationId,
        Math.max(this.#approvals.get(applicationId) ?? 0, timestamp)
      );
    }
  };

  /**
   * Requires every recorded ID to be found and current before accepting indexed approval state.
   * Checks IDs independently of queue filters and pagination when the initial result cannot prove it.
   * Employee callers supply companyId to retain the API's required tenant scope on ID checks.
   * Eight checks and the final refresh share a ten-second budget after the initial response.
   * Retry failure or exhaustion preserves the usable queue result, including pending work.
   * @throws when the initial request fails, leaving approval markers available for the next visit.
   */
  search = async (query: Record<string, unknown>, companyId?: string) => {
    let response = await this.#request(query);
    const approvals = this.#approvals;
    this.#approvals = new Map();
    if (!approvals.size || !this.#hasUnreconciledApproval(response, approvals)) {
      return response;
    }
    const deadline = Date.now() + RECONCILIATION_BUDGET_MS;
    const projectionQuery = {
      'model-type': 'TasProgramInstance',
      'search-body': {
        size: approvals.size,
        _source: ['tas_application_id_keyword', 'tas_application_latest_state_change_at_date'],
        query: {
          bool: {
            filter: [
              ...(companyId ? [{ term: { company_id: companyId } }] : []),
              { term: { record_type: 'application' } },
              { terms: { tas_application_id_keyword: [...approvals.keys()] } },
            ],
          },
        },
      },
    };

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        const backoff = Math.min(100 * 2 ** (attempt - 1), MAX_BACKOFF_MS);
        if (Date.now() + backoff >= deadline) {
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, backoff));
      }
      try {
        const projection = await this.#retry(projectionQuery, deadline);
        if (!this.#hasUnreconciledApproval(projection, approvals)) {
          response = await this.#retry(query, deadline);
          break;
        }
      } catch {
        break;
      }
    }
    return response;
  };

  #hasUnreconciledApproval = (response: OpenSearchResponse, approvals: Map<string, number>) => {
    const documents = OpenSearchResponseBuilder.from(
      response as OpenSearchResponse<ApplicationProjection>
    ).getDocuments();
    const timestamps = new Map(
      documents.map((document) => [
        document.tasApplicationIdKeyword,
        Date.parse(document.tasApplicationLatestStateChangeAtDate ?? ''),
      ])
    );
    return [...approvals].some(([id, approvedAt]) => {
      const indexedAt = timestamps.get(id) ?? NaN;
      return !Number.isFinite(indexedAt) || indexedAt < approvedAt;
    });
  };

  #request = async (query: Record<string, unknown>, signal?: AbortSignal) => {
    const { content } = await this.store.request<OpenSearchResponse>({
      ...executeSearchRequest(query),
      ...(signal && { signal }),
    });
    return content;
  };

  #retry = async (query: Record<string, unknown>, deadline: number) => {
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      throw new Error('Approval queue reconciliation timed out');
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), remaining);
    try {
      return await this.#request(query, controller.signal);
    } finally {
      clearTimeout(timer);
    }
  };
}
